import type { IReportMediaOptions } from '../types/reportsInterfaces';
import {
	extractPhotoSceneLabel,
	getPhotoSceneLabel,
	photoSrcMatchesMedia,
} from './reportPhoto';

export type AiOverlayKind = 'sku' | 'undefined' | 'zone' | 'pricetag';

export type AiOverlayBox = {
	name: string;
	skuId?: string;
	kind: AiOverlayKind;
	bbox: [number, number, number, number];
	sim?: number;
	conf?: string;
	faces?: number | null;
	price?: number | null;
};

export type AiOverlayLink = {
	skuId?: string;
	from: [number, number, number, number];
	to: [number, number, number, number];
};

export type AiFoundSku = {
	skuId?: string;
	name: string;
};

export type AiOverlayPhoto = {
	mediaId: number | string;
	w: number;
	h: number;
	scene?: string;
	boxes: AiOverlayBox[];
	links: AiOverlayLink[];
	foundSkus: AiFoundSku[];
};

type OverlayLayers = {
	undefinedSku: boolean;
	priceLinks: boolean;
	zones: boolean;
};

const OVERLAY_CLASS = 'ai-photo-overlay';
const BOX_CLASS = 'ai-photo-overlay__box';
const TIP_CLASS = 'ai-photo-overlay__tip';
const PANEL_CLASS = 'ai-photo-overlay-panel';
const MODAL_CLASS = 'photoviewer-modal--ai-controls';
const BTN_ACTIVE = 'photoviewer-button-aiRecognition--active';

const landscapeIcon = `<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1.5 3.5A1.5 1.5 0 0 1 3 2h10a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 13 14H3a1.5 1.5 0 0 1-1.5-1.5v-9zm1 0v9A.5.5 0 0 0 3 13h10a.5.5 0 0 0 .5-.5v-9A.5.5 0 0 0 13 3H3a.5.5 0 0 0-.5.5z"/><path fill="currentColor" d="M4.5 6.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm7.04 5.02-2.2-2.75a.5.5 0 0 0-.78 0L6.4 11.3l-.72-.9a.5.5 0 0 0-.78.05l-1.1 1.45A.5.5 0 0 0 4.2 13h7.6a.5.5 0 0 0 .34-.88z"/></svg>`;

const ZONE_NAME_RE = /^\s*z?\s*\d+\s*[-–.:)]\s*/i;
const ZONE_TYPE_RE = /zone|shelf|rack|полк/i;

let enabled = true;
let rafId = 0;
let attachTimer = 0;
let stageEl: HTMLElement | null = null;
let panelEl: HTMLElement | null = null;
let skuListEl: HTMLElement | null = null;
let sceneEl: HTMLElement | null = null;
let currentPhoto: AiOverlayPhoto | null = null;
let overlayPhotos: AiOverlayPhoto[] = [];
let overlayMedias: IReportMediaOptions[] = [];
let overlayAiReview: any = null;
let hiddenSkuKeys = new Set<string>();
let skuListOpen = true;
const layers: OverlayLayers = {
	undefinedSku: true,
	priceLinks: true,
	zones: true,
};

const formatPrice = (price: number | null | undefined): string | null => {
	if (price == null || Number.isNaN(Number(price))) {
		return null;
	}
	return `${Number(price)} Р`;
};

const formatSim = (sim: number | null | undefined): string | null => {
	if (sim == null || Number.isNaN(Number(sim))) {
		return null;
	}
	return `sim ${Number(sim).toFixed(3)}`;
};

const normalizeBbox = (
	bbox: number[]
): [number, number, number, number] | null => {
	if (!Array.isArray(bbox) || bbox.length < 4) {
		return null;
	}
	const [a, b, c, d] = bbox.map(Number);
	if ([a, b, c, d].some((n) => Number.isNaN(n))) {
		return null;
	}
	return [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];
};

const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');

const skuKey = (skuId?: string, name?: string): string =>
	skuId ? `id:${skuId}` : `name:${(name || '').trim().toLowerCase()}`;

const formatZoneName = (name: string, index: number): string => {
	const trimmed = String(name || '').trim();
	if (/^z\d+/i.test(trimmed)) {
		return trimmed;
	}
	const numbered = trimmed.match(/^(\d+)\s*[-–.:)]\s*(.*)$/);
	if (numbered) {
		return `Z${numbered[1]} - ${numbered[2] || 'Полка'}`;
	}
	return trimmed || `Z${index + 1} - Полка`;
};

const median = (values: number[]): number => {
	if (!values.length) {
		return 0;
	}
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2
		? sorted[mid]
		: (sorted[mid - 1] + sorted[mid]) / 2;
};

const deriveShelfZones = (boxes: AiOverlayBox[]): AiOverlayBox[] => {
	const products = boxes.filter(
		(box) => box.kind === 'sku' || box.kind === 'undefined'
	);
	if (!products.length) {
		return [];
	}
	const tags = boxes.filter((box) => box.kind === 'pricetag');
	const spanBoxes = [...products, ...tags];

	const toItem = (box: AiOverlayBox) => ({
		box,
		y1: box.bbox[1],
		y2: box.bbox[3],
		cy: (box.bbox[1] + box.bbox[3]) / 2,
		h: Math.max(1, box.bbox[3] - box.bbox[1]),
		w: Math.max(1, box.bbox[2] - box.bbox[0]),
	});

	const items = products.map(toItem).sort((a, b) => a.cy - b.cy);
	const medH = median(items.map((item) => item.h)) || items[0].h;
	const medW = median(items.map((item) => item.w)) || items[0].w;
	const anchors = items.filter(
		(item) => item.h >= medH * 0.55 && item.w >= medW * 0.4
	);
	const rowItems = anchors.length >= 2 ? anchors : items;

	const gaps: number[] = [];
	for (let i = 1; i < rowItems.length; i++) {
		gaps.push(rowItems[i].cy - rowItems[i - 1].cy);
	}
	const meanGap =
		gaps.reduce((sum, gap) => sum + gap, 0) / Math.max(gaps.length, 1);
	const stdGap = Math.sqrt(
		gaps.reduce((sum, gap) => sum + (gap - meanGap) ** 2, 0) /
			Math.max(gaps.length, 1)
	);
	const splitGap = Math.max(medH * 0.85, meanGap + stdGap);

	const clusters: Array<typeof items> = [];
	rowItems.forEach((item, index) => {
		if (index === 0 || rowItems[index].cy - rowItems[index - 1].cy > splitGap) {
			clusters.push([item]);
		} else {
			clusters[clusters.length - 1].push(item);
		}
	});

	const clusterCy = clusters.map(
		(cluster) =>
			cluster.reduce((sum, entry) => sum + entry.cy, 0) / cluster.length
	);
	const tagsByCluster: Array<Array<ReturnType<typeof toItem>>> = clusters.map(
		() => []
	);
	tags.map(toItem).forEach((tag) => {
		let best = 0;
		let bestDist = Number.POSITIVE_INFINITY;
		clusterCy.forEach((cy, index) => {
			const dist = Math.abs(tag.cy - cy);
			if (dist < bestDist) {
				bestDist = dist;
				best = index;
			}
		});
		tagsByCluster[best].push(tag);
	});

	const outerX1 = Math.min(...spanBoxes.map((box) => box.bbox[0]));
	const outerX2 = Math.max(...spanBoxes.map((box) => box.bbox[2]));
	const outerY1 = Math.min(...spanBoxes.map((box) => box.bbox[1]));
	const outerY2 = Math.max(...spanBoxes.map((box) => box.bbox[3]));
	const padX = Math.max(4, (outerX2 - outerX1) * 0.008);
	const padY = Math.max(4, medH * 0.04);

	const clusterMinY = (index: number) =>
		Math.min(...clusters[index].map((entry) => entry.y1));
	const clusterMaxY = (index: number) =>
		Math.max(
			...clusters[index].map((entry) => entry.y2),
			...tagsByCluster[index].map((entry) => entry.y2),
			clusterMinY(index)
		);

	const bounds: number[] = [outerY1 - padY];
	for (let i = 1; i < clusters.length; i++) {
		const prevMax = clusterMaxY(i - 1);
		const nextMin = clusterMinY(i);
		bounds.push(
			nextMin > prevMax
				? (prevMax + nextMin) / 2
				: (clusterCy[i - 1] + clusterCy[i]) / 2
		);
	}
	bounds.push(outerY2 + padY);

	return clusters.map((_, index) => ({
		name: `Z${index + 1} - Полка`,
		kind: 'zone' as const,
		bbox: [
			outerX1 - padX,
			bounds[index],
			outerX2 + padX,
			bounds[index + 1],
		] as [number, number, number, number],
	}));
};

const resolveSceneLabel = (photo: AiOverlayPhoto | null): string => {
	if (!photo) {
		return '';
	}
	if (photo.scene) {
		return photo.scene;
	}
	const media = overlayMedias.find(
		(item) => String(item?.media?.id) === String(photo.mediaId)
	);
	return getPhotoSceneLabel(media, overlayAiReview);
};

const isZoneBox = (box: any): boolean => {
	if (
		box?.isZone === true ||
		box?.zone === true ||
		box?.layer === 'zone' ||
		box?.role === 'zone'
	) {
		return true;
	}
	const type = String(
		box?.type ?? box?.kind ?? box?.class ?? box?.category ?? ''
	);
	if (ZONE_TYPE_RE.test(type) && !/sku|product|price/i.test(type)) {
		return true;
	}
	const name = String(box?.name || box?.label || '');
	return (
		ZONE_NAME_RE.test(name) ||
		(/^z\d+/i.test(name) && !box?.skuId) ||
		(/^полка\b/i.test(name) && !box?.skuId)
	);
};

const toOverlayBox = (
	box: any,
	kind: AiOverlayKind,
	extra: Partial<AiOverlayBox> = {}
): AiOverlayBox | null => {
	const bbox = normalizeBbox(box?.bbox || box?.box || box?.rect);
	if (!bbox) {
		return null;
	}
	const name = String(box?.name || box?.label || extra.name || '');
	if (!name && kind !== 'pricetag') {
		return null;
	}
	return {
		name,
		skuId: box?.skuId != null ? String(box.skuId) : extra.skuId,
		kind,
		bbox,
		sim: typeof box.sim === 'number' ? box.sim : extra.sim,
		conf: box.conf ?? extra.conf,
		faces: extra.faces ?? null,
		price:
			typeof box.price === 'number'
				? box.price
				: extra.price ?? null,
	};
};

/** Собирает фото AI с боксами, зонами, ценниками и связями товар–цена. */
export const buildAiOverlayPhotos = (aiReview: any): AiOverlayPhoto[] => {
	if (!aiReview || !Array.isArray(aiReview.photos)) {
		return [];
	}

	const skus = Array.isArray(aiReview.skus) ? aiReview.skus : [];

	return aiReview.photos
		.map((photo: any) => {
			const mediaId = photo?.mediaId ?? photo?.media?.id;
			if (mediaId == null) {
				return null;
			}
			const rawBoxes = Array.isArray(photo.boxes) ? photo.boxes : [];
			const pricetags = Array.isArray(photo.pricetags)
				? photo.pricetags
				: [];
			const rawZones = [
				...(Array.isArray(photo.zones) ? photo.zones : []),
				...(Array.isArray(photo.shelves) ? photo.shelves : []),
				...(Array.isArray(photo.areas) ? photo.areas : []),
				...(Array.isArray(photo.racks) ? photo.racks : []),
				...(Array.isArray(photo.zoneBoxes) ? photo.zoneBoxes : []),
				...(Array.isArray(photo.shelfZones) ? photo.shelfZones : []),
				...(Array.isArray(photo.planogramZones)
					? photo.planogramZones
					: []),
				...(Array.isArray(photo.regions) ? photo.regions : []),
			];

			const boxes: AiOverlayBox[] = [];

			rawBoxes.forEach((box: any) => {
				if (isZoneBox(box)) {
					const zone = toOverlayBox(box, 'zone', {
						name: formatZoneName(String(box?.name || ''), boxes.length),
					});
					if (zone) {
						boxes.push(zone);
					}
					return;
				}
				const skuId = box.skuId != null ? String(box.skuId) : undefined;
				const sku = skus.find(
					(s: any) =>
						(skuId && String(s.skuId) === skuId) ||
						s?.name === box.name
				);
				const appears = (sku?.appearsIn || []).find(
					(item: { mediaId?: number | string }) =>
						String(item.mediaId) === String(mediaId)
				);
				const tag = pricetags.find(
					(t: any) =>
						(skuId && String(t.skuId) === skuId) ||
						t?.name === box.name
				);
				const facesFromBoxes = rawBoxes.filter(
					(b: any) =>
						(skuId && String(b.skuId) === skuId) ||
						b?.name === box.name
				).length;
				const kind: AiOverlayKind =
					skuId || sku ? 'sku' : 'undefined';
				const overlayBox = toOverlayBox(box, kind, {
					name: String(box?.name || sku?.name || ''),
					skuId,
					faces:
						appears?.facesHere ??
						sku?.faces ??
						(facesFromBoxes || null),
					price:
						typeof tag?.price === 'number'
							? tag.price
							: typeof sku?.price === 'number'
								? sku.price
								: null,
				});
				if (overlayBox) {
					boxes.push(overlayBox);
				}
			});

			rawZones.forEach((zone: any, index: number) => {
				const overlayBox = toOverlayBox(zone, 'zone', {
					name: formatZoneName(
						String(zone?.name || zone?.label || ''),
						index
					),
				});
				if (overlayBox) {
					boxes.push(overlayBox);
				}
			});

			pricetags.forEach((tag: any) => {
				const skuId = tag.skuId != null ? String(tag.skuId) : undefined;
				const sku = skus.find(
					(s: any) => skuId && String(s.skuId) === skuId
				);
				const overlayBox = toOverlayBox(tag, 'pricetag', {
					name: sku?.name
						? `Ценник: ${sku.name}`
						: `Ценник${skuId ? ` #${skuId}` : ''}`,
					skuId,
					price:
						typeof tag.price === 'number'
							? tag.price
							: typeof sku?.price === 'number'
								? sku.price
								: null,
				});
				if (overlayBox) {
					boxes.push(overlayBox);
				}
			});

			if (!boxes.some((box) => box.kind === 'zone')) {
				boxes.push(...deriveShelfZones(boxes));
			}

			const explicitLinks = [
				...(Array.isArray(photo.links) ? photo.links : []),
				...(Array.isArray(photo.relations) ? photo.relations : []),
				...(Array.isArray(photo.priceLinks) ? photo.priceLinks : []),
			];
			const links: AiOverlayLink[] = [];
			explicitLinks.forEach((link: any) => {
				const from =
					normalizeBbox(link.from || link.skuBbox || link.product) ||
					null;
				const to =
					normalizeBbox(link.to || link.priceBbox || link.pricetag) ||
					null;
				if (from && to) {
					links.push({
						skuId:
							link.skuId != null ? String(link.skuId) : undefined,
						from,
						to,
					});
				}
			});
			if (!links.length) {
				const skuBoxes = boxes.filter((box) => box.kind === 'sku');
				const tagBoxes = boxes.filter((box) => box.kind === 'pricetag');
				tagBoxes.forEach((tag) => {
					const match = skuBoxes.find(
						(skuBox) =>
							(tag.skuId && skuBox.skuId === tag.skuId) ||
							(!tag.skuId &&
								skuBox.name &&
								tag.name.includes(skuBox.name))
					);
					if (match) {
						links.push({
							skuId: tag.skuId || match.skuId,
							from: match.bbox,
							to: tag.bbox,
						});
					}
				});
			}

			const foundMap = new Map<string, AiFoundSku>();
			boxes
				.filter((box) => box.kind === 'sku')
				.forEach((box) => {
					const key = skuKey(box.skuId, box.name);
					if (!foundMap.has(key)) {
						foundMap.set(key, {
							skuId: box.skuId,
							name: box.name,
						});
					}
				});
			skus.forEach((sku: any) => {
				const onPhoto = (sku?.appearsIn || []).some(
					(item: { mediaId?: number | string }) =>
						String(item.mediaId) === String(mediaId)
				);
				if (!onPhoto && !sku?.skuId) {
					return;
				}
				if (!onPhoto) {
					return;
				}
				const item: AiFoundSku = {
					skuId: sku.skuId != null ? String(sku.skuId) : undefined,
					name: String(sku.name || ''),
				};
				if (!item.name) {
					return;
				}
				foundMap.set(skuKey(item.skuId, item.name), item);
			});

			let w = Number(photo.w) || Number(photo.width) || 0;
			let h = Number(photo.h) || Number(photo.height) || 0;
			if (!w || !h) {
				boxes.forEach((box) => {
					w = Math.max(w, box.bbox[2]);
					h = Math.max(h, box.bbox[3]);
				});
			}

			return {
				mediaId,
				w,
				h,
				scene: extractPhotoSceneLabel(photo, [
					photo.taskName,
					photo.task,
					photo.name,
				]),
				boxes,
				links,
				foundSkus: Array.from(foundMap.values()),
			};
		})
		.filter(Boolean) as AiOverlayPhoto[];
};

/** Находит AI-оверлей фото по mediaId (для превью в сетке). */
export const findAiOverlayPhoto = (
	aiReview: any,
	mediaId: number | string | undefined | null
): AiOverlayPhoto | null => {
	if (mediaId == null) {
		return null;
	}
	const photos = buildAiOverlayPhotos(aiReview);
	return (
		photos.find((photo) => String(photo.mediaId) === String(mediaId)) || null
	);
};

const matchMediaIdBySrc = (
	src: string,
	medias: IReportMediaOptions[]
): string | null => {
	if (!src || !medias?.length) {
		return null;
	}
	const found = medias.find((media) => photoSrcMatchesMedia(src, media));
	return found?.media?.id != null ? String(found.media.id) : null;
};

const findOverlayPhoto = (
	photos: AiOverlayPhoto[],
	medias: IReportMediaOptions[],
	src: string
): AiOverlayPhoto | null => {
	const mediaId = matchMediaIdBySrc(src, medias);
	if (mediaId) {
		const byId = photos.find((p) => String(p.mediaId) === mediaId);
		if (byId) {
			return byId;
		}
	}
	return null;
};

const clearOverlay = () => {
	if (!stageEl) {
		return;
	}
	stageEl.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove());
};

const buildTipHtml = (box: AiOverlayBox): string => {
	const lines = [`<div class="${TIP_CLASS}-name">${escapeHtml(box.name)}</div>`];
	if (box.faces != null) {
		lines.push(
			`<div class="${TIP_CLASS}-row">Фейсов: ${escapeHtml(String(box.faces))}</div>`
		);
	}
	const price = formatPrice(box.price);
	if (price) {
		lines.push(
			`<div class="${TIP_CLASS}-row">Цена: ${escapeHtml(price)}</div>`
		);
	}
	const sim = formatSim(box.sim);
	if (sim) {
		lines.push(`<div class="${TIP_CLASS}-row">${escapeHtml(sim)}</div>`);
	}
	return lines.join('');
};

const isBoxVisible = (box: AiOverlayBox): boolean => {
	if (box.kind === 'undefined') {
		return layers.undefinedSku;
	}
	if (box.kind === 'zone') {
		return layers.zones;
	}
	if (box.kind === 'pricetag') {
		return layers.priceLinks;
	}
	return !hiddenSkuKeys.has(skuKey(box.skuId, box.name));
};

const renderBoxes = (overlay: HTMLElement, photo: AiOverlayPhoto) => {
	overlay.innerHTML = '';
	if (layers.priceLinks && photo.links.length) {
		const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		svg.setAttribute('class', 'ai-photo-overlay__links');
		svg.setAttribute('viewBox', `0 0 ${photo.w || 100} ${photo.h || 100}`);
		svg.setAttribute('preserveAspectRatio', 'none');
		photo.links.forEach((link) => {
			const hidden =
				(link.skuId && hiddenSkuKeys.has(`id:${link.skuId}`)) ||
				false;
			if (hidden) {
				return;
			}
			const [x1, y1, x2, y2] = link.from;
			const [tx1, ty1, tx2, ty2] = link.to;
			const line = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'line'
			);
			line.setAttribute('x1', String((x1 + x2) / 2));
			line.setAttribute('y1', String((y1 + y2) / 2));
			line.setAttribute('x2', String((tx1 + tx2) / 2));
			line.setAttribute('y2', String((ty1 + ty2) / 2));
			svg.appendChild(line);
		});
		overlay.appendChild(svg);
	}

	[...photo.boxes]
		.sort(
			(a, b) =>
				Number(a.kind !== 'zone') - Number(b.kind !== 'zone')
		)
		.forEach((box) => {
		if (!isBoxVisible(box)) {
			return;
		}
		const [x1, y1, x2, y2] = box.bbox;
		const w = photo.w || 1;
		const h = photo.h || 1;
		const el = document.createElement('div');
		el.className = `${BOX_CLASS} ${BOX_CLASS}--${box.kind}`;
		el.style.left = `${(x1 / w) * 100}%`;
		el.style.top = `${(y1 / h) * 100}%`;
		el.style.width = `${((x2 - x1) / w) * 100}%`;
		el.style.height = `${((y2 - y1) / h) * 100}%`;

		if (box.kind === 'zone') {
			const label = document.createElement('div');
			label.className = 'ai-photo-overlay__zone-label';
			label.textContent = box.name;
			el.appendChild(label);
		} else {
			const tip = document.createElement('div');
			tip.className = TIP_CLASS;
			tip.innerHTML = buildTipHtml(box);
			el.appendChild(tip);
		}
		overlay.appendChild(el);
	});
};

const syncOverlayToImage = () => {
	if (!stageEl || !enabled || !currentPhoto) {
		clearOverlay();
		return;
	}
	const img = stageEl.querySelector(
		'.photoviewer-image'
	) as HTMLImageElement | null;
	if (!img || !img.complete) {
		return;
	}

	const stageRect = stageEl.getBoundingClientRect();
	const imgRect = img.getBoundingClientRect();
	if (imgRect.width < 2 || imgRect.height < 2) {
		return;
	}

	let overlay = stageEl.querySelector(
		`.${OVERLAY_CLASS}`
	) as HTMLElement | null;
	if (!overlay) {
		overlay = document.createElement('div');
		overlay.className = OVERLAY_CLASS;
		stageEl.appendChild(overlay);
		renderBoxes(overlay, currentPhoto);
	}

	overlay.style.display = 'block';
	overlay.style.left = `${imgRect.left - stageRect.left}px`;
	overlay.style.top = `${imgRect.top - stageRect.top}px`;
	overlay.style.width = `${imgRect.width}px`;
	overlay.style.height = `${imgRect.height}px`;
};

const startSyncLoop = () => {
	stopSyncLoop();
	const tick = () => {
		syncOverlayToImage();
		rafId = window.requestAnimationFrame(tick);
	};
	rafId = window.requestAnimationFrame(tick);
};

const stopSyncLoop = () => {
	if (rafId) {
		window.cancelAnimationFrame(rafId);
		rafId = 0;
	}
};

const setButtonActive = (active: boolean) => {
	const btn = document.querySelector(
		'.photoviewer-button-aiRecognition'
	) as HTMLElement | null;
	if (!btn) {
		return;
	}
	btn.classList.toggle(BTN_ACTIVE, active);
	btn.setAttribute('aria-pressed', active ? 'true' : 'false');
};

const resolveSrcFromContext = (context: any): string => {
	const fromImage = context?.imageData?.src || context?.imageData?.url;
	if (fromImage) {
		return String(fromImage);
	}
	const img = document.querySelector(
		'.photoviewer-stage .photoviewer-image'
	) as HTMLImageElement | null;
	return img?.currentSrc || img?.src || '';
};

const redrawCurrent = () => {
	clearOverlay();
	if (enabled && currentPhoto && stageEl) {
		const overlay = document.createElement('div');
		overlay.className = OVERLAY_CLASS;
		stageEl.appendChild(overlay);
		renderBoxes(overlay, currentPhoto);
		syncOverlayToImage();
	}
};

const updatePanel = () => {
	if (!panelEl) {
		return;
	}
	const count = currentPhoto?.foundSkus.length || 0;
	const list = currentPhoto?.foundSkus || [];
	const switchRow = (layer: keyof OverlayLayers, label: string) => `
		<label class="${PANEL_CLASS}__item">
			<span>${label}</span>
			<span class="${PANEL_CLASS}__switch">
				<input type="checkbox" data-layer="${layer}" ${layers[layer] ? 'checked' : ''}/>
				<span class="${PANEL_CLASS}__slider"></span>
			</span>
		</label>
	`;
	panelEl.innerHTML = `
		${switchRow('undefinedSku', 'Показывать неопределённые SKU')}
		${switchRow('priceLinks', 'связи товар — цена')}
		${switchRow('zones', 'Показывать зоны')}
		<button type="button" class="${PANEL_CLASS}__skus" aria-expanded="${skuListOpen}">
			Найдено SKU: ${count}
		</button>
	`;
	if (skuListEl) {
		skuListEl.hidden = !skuListOpen;
		skuListEl.innerHTML = list.length
			? list
					.map((sku) => {
						const key = skuKey(sku.skuId, sku.name);
						const checked = !hiddenSkuKeys.has(key);
						return `<label class="${PANEL_CLASS}__sku">
							<input type="checkbox" data-sku="${escapeHtml(key)}" ${checked ? 'checked' : ''}/>
							<span>${escapeHtml(sku.name)}${sku.skuId ? ` · SKU ${escapeHtml(sku.skuId)}` : ''}</span>
						</label>`;
					})
					.join('')
			: `<div class="${PANEL_CLASS}__empty">SKU не найдены</div>`;
	}
	if (sceneEl) {
		const scene = resolveSceneLabel(currentPhoto);
		sceneEl.textContent = scene ? `Сцена: ${scene}` : '';
		sceneEl.hidden = !scene;
	}
};

const onPanelClick = (event: Event) => {
	const target = event.target as HTMLElement | null;
	if (!target) {
		return;
	}
	const skuBtn = target.closest(`.${PANEL_CLASS}__skus`);
	if (skuBtn) {
		skuListOpen = !skuListOpen;
		updatePanel();
	}
};

const onPanelChange = (event: Event) => {
	const target = event.target as HTMLInputElement | null;
	if (!target) {
		return;
	}
	if (target.dataset.layer) {
		const layer = target.dataset.layer as keyof OverlayLayers;
		if (layer && layer in layers) {
			layers[layer] = target.checked;
			redrawCurrent();
		}
		return;
	}
	if (target.dataset.sku) {
		if (target.checked) {
			hiddenSkuKeys.delete(target.dataset.sku);
		} else {
			hiddenSkuKeys.add(target.dataset.sku);
		}
		redrawCurrent();
	}
};

const mountPanel = (): boolean => {
	const modal = document.querySelector('.photoviewer-modal') as HTMLElement | null;
	const footer = document.querySelector('.photoviewer-footer') as HTMLElement | null;
	const stage =
		stageEl ||
		(document.querySelector('.photoviewer-stage') as HTMLElement | null);
	if (!modal) {
		return false;
	}
	stageEl = stage;
	modal.classList.add(MODAL_CLASS);

	if (!panelEl) {
		panelEl = document.createElement('div');
		panelEl.className = PANEL_CLASS;
		panelEl.addEventListener('click', onPanelClick);
		panelEl.addEventListener('change', onPanelChange);
		if (footer) {
			footer.appendChild(panelEl);
		} else {
			panelEl.classList.add(`${PANEL_CLASS}--floating`);
			modal.appendChild(panelEl);
		}
	}

	if (stage && !sceneEl) {
		sceneEl = document.createElement('div');
		sceneEl.className = `${PANEL_CLASS}__scene`;
		stage.appendChild(sceneEl);
	}

	if (stage && !skuListEl) {
		skuListEl = document.createElement('div');
		skuListEl.className = `${PANEL_CLASS}__sku-list`;
		skuListEl.addEventListener('change', onPanelChange);
		stage.appendChild(skuListEl);
	}

	updatePanel();
	return Boolean(stage);
};

const removePanel = () => {
	const modal = document.querySelector('.photoviewer-modal');
	modal?.classList.remove(MODAL_CLASS);
	if (panelEl) {
		panelEl.removeEventListener('click', onPanelClick);
		panelEl.removeEventListener('change', onPanelChange);
		panelEl.remove();
		panelEl = null;
	}
	if (skuListEl) {
		skuListEl.removeEventListener('change', onPanelChange);
		skuListEl.remove();
		skuListEl = null;
	}
	if (sceneEl) {
		sceneEl.remove();
		sceneEl = null;
	}
};

const applyForSrc = (src: string) => {
	const next = findOverlayPhoto(overlayPhotos, overlayMedias, src);
	if (!currentPhoto || String(currentPhoto.mediaId) !== String(next?.mediaId)) {
		hiddenSkuKeys = new Set();
		skuListOpen = true;
	}
	currentPhoto = next;
	redrawCurrent();
	updatePanel();
};

export type AttachAiOverlayArgs = {
	context: any;
	medias: IReportMediaOptions[];
	aiReview: any;
	/** Показывать оверлей по умолчанию */
	defaultEnabled?: boolean;
};

/** Подключает оверлей распознавания к открытому PhotoViewer. */
export const attachAiPhotoOverlay = ({
	context,
	medias,
	aiReview,
	defaultEnabled = true,
}: AttachAiOverlayArgs): void => {
	window.clearTimeout(attachTimer);
	detachAiPhotoOverlay();

	overlayPhotos = buildAiOverlayPhotos(aiReview);
	overlayMedias = medias || [];
	overlayAiReview = aiReview;
	enabled = defaultEnabled;
	setButtonActive(enabled);

	const tryMount = (attempt: number) => {
		stageEl =
			(document.querySelector('.photoviewer-stage') as HTMLElement) ||
			null;
		if (!mountPanel()) {
			if (attempt < 25) {
				attachTimer = window.setTimeout(
					() => tryMount(attempt + 1),
					40
				);
			}
			return;
		}
		applyForSrc(resolveSrcFromContext(context));
		startSyncLoop();
	};

	tryMount(0);
};

export const refreshAiPhotoOverlay = (
	context: any,
	medias: IReportMediaOptions[],
	aiReview: any
) => {
	overlayPhotos = buildAiOverlayPhotos(aiReview);
	overlayMedias = medias || [];
	overlayAiReview = aiReview;
	if (!stageEl) {
		stageEl =
			(document.querySelector('.photoviewer-stage') as HTMLElement) ||
			null;
	}
	if (!panelEl || !skuListEl) {
		mountPanel();
	}
	applyForSrc(resolveSrcFromContext(context));
};

export const toggleAiPhotoOverlay = (): boolean => {
	enabled = !enabled;
	setButtonActive(enabled);
	if (!enabled) {
		clearOverlay();
	} else {
		redrawCurrent();
	}
	return enabled;
};

export const detachAiPhotoOverlay = () => {
	window.clearTimeout(attachTimer);
	stopSyncLoop();
	clearOverlay();
	removePanel();
	stageEl = null;
	currentPhoto = null;
	overlayPhotos = [];
	overlayMedias = [];
	overlayAiReview = null;
	hiddenSkuKeys = new Set();
	skuListOpen = true;
};

export const getAiRecognitionToolbarOptions = () => ({
	footerToolbar: [
		'zoomOut',
		'zoomIn',
		'prev',
		'fullscreen',
		'next',
		'actualSize',
		'rotateLeft',
		'rotateRight',
	] as string[],
	customButtons: {
		aiRecognition: {
			text: landscapeIcon,
			title: 'Распознавание ИИ',
			click: () => {
				toggleAiPhotoOverlay();
			},
		},
	},
	icons: {
		aiRecognition: landscapeIcon,
	},
	i18n: {
		aiRecognition: 'Распознавание ИИ',
	},
});
